/* eslint-disable @typescript-eslint/no-floating-promises, parog-web/no-glint-expect-error-directive */
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import perform from 'ember-concurrency/helpers/perform';
import formatDate from 'ember-intl/helpers/format-date';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import type { DmpDocumentContent } from 'parog-web/cnam/types';
import { DocumentVisibility } from 'parog-web/cnam/types';
import FileAttachmentDownloader from 'parog-web/components/file-attachment-downloader';
import FileDisplayer from 'parog-web/components/file-displayer';
import type DmpDocumentMetadata from 'parog-web/models/dmp-document-metadata';
import emberSwal from 'ui-kit/utils/ember-swal';

interface Args {
  metadata: DmpDocumentMetadata;
  content: DmpDocumentContent;
  isRunning: boolean;
  returnToDocuments: () => Promise<void>;
}

export default class DocumentsRetrieveAccessFormShowContentComponent extends Component<Args> {
  @tracked shouldShowVisibilityAlert = true;

  get isAddedByPatient(): boolean {
    return this.args.metadata?.classCode == '90';
  }

  get isHiddenToLegalRepresentatives(): boolean {
    return this.args.metadata?.confidentialities.includes(
      DocumentVisibility.HIDDEN_TO_LEGAL_REPRESENTATIVES
    );
  }

  get isHiddenToPatient(): boolean {
    return this.args.metadata?.confidentialities.includes(
      DocumentVisibility.HIDDEN_TO_PATIENT
    );
  }

  get isRunning(): boolean {
    return this.args.isRunning || this.shouldShowVisibilityAlert;
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);
    if (
      (this.isHiddenToPatient || this.isHiddenToLegalRepresentatives) &&
      this.shouldShowVisibilityAlert
    ) {
      const title = `Vous allez consulter un document ${
        this.isHiddenToPatient
          ? 'non visible par le patient'
          : 'non visible par les représentants légaux'
      }`;
      emberSwal
        .fire({
          title,
          text: 'Êtes-vous sûr de vouloir le consulter ?',
          icon: 'warning',
          cancelButtonText: 'Consulter',
          showCancelButton: true,
          confirmButtonText: 'Revenir à la liste de documents',
          confirmButtonColor: '#d33'
        })
        .then(result => {
          if (result.isConfirmed) {
            this.args.returnToDocuments();
          } else {
            this.shouldShowVisibilityAlert = false;
          }
        });
    } else {
      this.shouldShowVisibilityAlert = false;
    }
  }

  <template>
    <div class='mb-[20px]' data-test-dmp-document-content>
      <div class='time-label' data-test-dmp-document-created-at>
        Créé le
        {{formatDate
          @metadata.creationTime
          weekday='long'
          month='long'
          day='2-digit'
          year='numeric'
          hour='2-digit'
          minute='2-digit'
        }}.
        <div
          class='semeia primary-color font-size mb-4 text-[1.3em] text-black'
        >
          {{#if this.isHiddenToPatient}}
            Document non visible par le patient.
          {{/if}}
          {{#if this.isAddedByPatient}}
            Document ajouté par le patient.
          {{/if}}
          {{#if this.isHiddenToLegalRepresentatives}}
            Attention, ce document n’est pas visible des représentants légaux
            pour préserver le secret du mineur titulaire du DMP
          {{/if}}
        </div>
      </div>
      <p>

        {{! @glint-expect-error }}
        {{@metadata.comments}}
      </p>

      {{! @glint-expect-error }}
      {{#if @showHeaders}}
        <FileDisplayer
          {{! @glint-expect-error }}
          @mimeType={{@content.cdaHeadersMimeType}}
          @isRunning={{this.isRunning}}
          @documentContent={{@content.cdaHeaders}}
          @iframeTitle='CDA-R2 Headers'
        />
      {{else}}
        <FileDisplayer
          {{! @glint-expect-error }}
          @mimeType={{@content.bodyMimeType}}
          @isRunning={{this.isRunning}}
          @documentContent={{@content.body}}
          @iframeTitle='DMP Document'
        />
        {{#if (and (not this.isRunning) @content.bodyMimeType)}}
          <FileAttachmentDownloader as |d|>
            {{! prettier-ignore }}
            <button
              type='button'
              {{! @glint-expect-error }}
              class='ui pink tertiary icon button {{if d.downloadFileFromContentTask.isRunning "loading"}}'
              data-test-file-link
              {{! @glint-expect-error }}
              {{on 'click' (perform d.downloadFileFromContentTask
                  (concat
                    'data:' @content.bodyMimeType ';base64,' @content.body
                  )
                  @content.bodyMimeType
                  @content.title
                )
              }}
            >
              <i class='file icon'></i>
              Télécharger le document
            </button>
          </FileAttachmentDownloader>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
